"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeUndefined = exports.isDeepEqual = exports.deepClone = void 0;
/**
 * Gets the type name using toString approach.
 *
 * Extracts actual type name from this standard format: [object XXXX]
 */
const getTypeName = (obj) => Object.prototype.toString.call(obj).slice(8, -1);
const cloners = {
    Object: (obj) => Object.keys(obj).reduce((clone, key) => ((clone[key] = (0, exports.deepClone)(obj[key])), clone), {}),
    Array: (obj) => obj.map((item) => (0, exports.deepClone)(item)),
    Date: (obj) => new Date(obj.getTime()),
    RegExp: (obj) => new RegExp(obj),
    Set: (obj) => new Set([...obj].map((value) => (0, exports.deepClone)(value))),
    Map: (obj) => new Map([...obj].map((kv) => [(0, exports.deepClone)(kv[0]), (0, exports.deepClone)(kv[1])])),
};
/**
 * Naive-ish deep clone. Handles cloning for `SupportedObjectTypes`. Everything else is returned as-is.
 *
 * No circular reference detection. If
 */
const deepClone = (obj) => {
    const cloner = cloners[getTypeName(obj)];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!cloner) {
        // the `as SupportedObjectTypes` above means it could be falsey
        return obj;
    }
    return cloner(obj);
};
exports.deepClone = deepClone;
const defaultComparer = (a, b) => a === b;
const comparers = {
    Object: (a, b) => {
        const obj1Keys = Object.keys(a);
        if (obj1Keys.length !== Object.keys(b).length)
            return false;
        for (const key of obj1Keys) {
            if (!(0, exports.isDeepEqual)(a[key], b[key])) {
                return false; // short-circuit on first failure
            }
        }
        return true;
    },
    Array: (a, b) => {
        if (a.length !== b.length) {
            return false;
        }
        // each item in each index must be equal, by position
        for (let i = 0; i < a.length; i++) {
            if (!(0, exports.isDeepEqual)(a[i], b[i])) {
                return false; // short-circuit on first failure
            }
        }
        return true; // all match by index
    },
    Date: (a, b) => a.getTime() === b.getTime(),
    RegExp: (a, b) => a.source === b.source && a.flags === b.flags,
    Map: (a, b) => {
        if (a.size !== b.size)
            return false;
        for (const [name, value] of a.entries()) {
            if (!b.has(name)) {
                return false;
            }
            if (!(0, exports.isDeepEqual)(value, b.get(name))) {
                return false;
            }
        }
        return true;
    },
    Set: (a, b) => {
        if (a.size !== b.size)
            return false;
        for (const value of a.values()) {
            if (!b.has(value)) {
                return false;
            }
        }
        return true;
    },
};
/**
 * Gets if given values match completely/deeply.
 *
 * Handles comparing for `SupportedObjectTypes`.
 * Everything else uses strict equality of the two given.
 *
 * For arrays, all elements must be deepEqual by position.
 * */
const isDeepEqual = (a, b) => {
    var _a;
    const compareType = getTypeName(a);
    if (compareType !== getTypeName(b)) {
        return false;
    }
    // the `as SupportedObjectTypes` below means it could be falsey
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const comparer = (_a = comparers[compareType]) !== null && _a !== void 0 ? _a : defaultComparer;
    return comparer(a, b);
};
exports.isDeepEqual = isDeepEqual;
const removers = {
    Object: (obj, removeEmpty) => {
        const result = Object.entries(obj).reduce((cleanedObj, [key, value]) => {
            const cleaned = (0, exports.removeUndefined)(value, removeEmpty);
            if (cleaned !== undefined) {
                cleanedObj[key] = cleaned;
            }
            return cleanedObj;
        }, {});
        if (removeEmpty && Object.keys(result).length === 0) {
            return undefined;
        }
        return result;
    },
    Array: (obj, removeEmpty) => {
        const result = obj.reduce((newArray, value) => {
            const cleaned = (0, exports.removeUndefined)(value, removeEmpty);
            if (cleaned !== undefined) {
                newArray.push(cleaned);
            }
            return newArray;
        }, []);
        if (removeEmpty && result.length === 0) {
            return undefined;
        }
        return result;
    },
    Map: (map, removeEmpty) => {
        const newMap = new Map();
        for (const [key, value] of map) {
            const cleaned = (0, exports.removeUndefined)(value, removeEmpty);
            if (cleaned !== undefined) {
                newMap.set(key, cleaned);
            }
        }
        if (removeEmpty && newMap.size === 0) {
            return undefined;
        }
        return newMap;
    },
    Set: (set, removeEmpty) => {
        const newSet = new Set();
        for (const value of set) {
            const cleaned = (0, exports.removeUndefined)(value, removeEmpty);
            if (cleaned !== undefined) {
                newSet.add(cleaned);
            }
        }
        if (removeEmpty && newSet.size === 0) {
            return undefined;
        }
        return newSet;
    },
};
/**
 * Recursively/deeply removes properties that are undefined for objects, maps, sets,
 * and removes undefined values from arrays.
 *
 * Returns a NEW object/array/map/set as needed to do each of these.
 *
 * If given value is not a supported type, it is returned as-is.
 *
 * @param obj - object to strip undefineds from
 * @param removeEmpty - if true, will remove empty objects/arrays/maps/sets and return undefined if no props remain
 *
 * @returns same object without undefined props
 */
const removeUndefined = (obj, removeEmpty) => {
    const remover = removers[getTypeName(obj)];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!remover) {
        // the `as SupportedRemovers` above means it could be falsey
        return obj;
    }
    return remover(obj, removeEmpty !== null && removeEmpty !== void 0 ? removeEmpty : false);
};
exports.removeUndefined = removeUndefined;
